// Numbers, money, dates and plain words for the API's values (front/RULES.md).
import type { Invoice, InvoiceStatus, Role } from "../api/types";

export function money(value: number | null | undefined, currency = "USD"): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  } catch {
    return "$" + amount(value);
  }
}

/** A headline figure: whole dollars, and from 100,000 up "$212K" / "$1.2M" (Reports tiles). */
export function compactMoney(value: number | null | undefined, currency = "USD"): string {
  if (value == null) return "—";
  const abs = Math.abs(value);
  if (abs >= 100_000) return new Intl.NumberFormat("en-US", { style: "currency", currency, notation: "compact", maximumFractionDigits: abs >= 1_000_000 ? 1 : 0 }).format(value);
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
}

export function amount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function signed(value: number): string {
  return (value < 0 ? "−" : "+") + amount(Math.abs(value));
}

export function percent(rate: number | null | undefined, digits = 0): string {
  if (rate === null || rate === undefined) return "—";
  return (rate * 100).toFixed(digits) + "%";
}

export function count(n: number, singular: string, plural = singular + "s"): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? singular : plural}`;
}

const dayFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const dayYearFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const timeFormat = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
const weekdayFormat = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });
const longDateFormat = new Intl.DateTimeFormat("en-US", { weekday: "long", day: "numeric", month: "long" });

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isToday(d: Date, now = new Date()): boolean {
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

export function isYesterday(d: Date, now = new Date()): boolean {
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  return isToday(d, y);
}

export function fmtTime(value: string | Date | null | undefined): string {
  const d = value instanceof Date ? value : parseDate(value);
  return d ? timeFormat.format(d) : "—";
}

export function fmtDay(value: string | Date | null | undefined, now = new Date()): string {
  const d = value instanceof Date ? value : parseDate(value);
  if (!d) return "—";
  if (isToday(d, now)) return "Today";
  if (isYesterday(d, now)) return "Yesterday";
  return d.getFullYear() === now.getFullYear() ? dayFormat.format(d) : dayYearFormat.format(d);
}

/** A moment, in the prototype's stamp form: "Oct 19, 1:13 AM" (see fmtStamp). */
export function fmtWhen(value: string | Date | null | undefined, now = new Date()): string {
  return fmtStamp(value, now);
}

export function fmtDateTime(value: string | Date | null | undefined): string {
  const d = value instanceof Date ? value : parseDate(value);
  return d ? dayYearFormat.format(d) + ", " + timeFormat.format(d) : "—";
}

/** The prototype's stamp: "Oct 14, 9:40 AM" (the year only when it isn't this one). */
export function fmtStamp(value: string | Date | null | undefined, now = new Date()): string {
  const d = value instanceof Date ? value : parseDate(value);
  if (!d) return "—";
  return (d.getFullYear() === now.getFullYear() ? dayFormat.format(d) : dayYearFormat.format(d)) + ", " + timeFormat.format(d);
}

export function fmtWeekday(d: Date): string {
  return weekdayFormat.format(d);
}

/** "Sunday 25 October": the date beside "Today" on the Overview. */
export function fmtDateLong(d: Date): string {
  const parts = longDateFormat.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")}`;
}

/** "4 min", "3 h", "2 d": how long since. */
export function ago(value: string | Date | null | undefined, now = new Date()): string {
  const d = value instanceof Date ? value : parseDate(value);
  if (!d) return "—";
  return duration(now.getTime() - d.getTime());
}

export function duration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return s + " s";
  const m = Math.round(s / 60);
  if (m < 60) return m + " min";
  const h = Math.round(m / 60);
  if (h < 48) return h + " h";
  return Math.round(h / 24) + " d";
}

export function hoursSince(value: string | null | undefined, now = new Date()): number {
  const d = parseDate(value);
  return d ? (now.getTime() - d.getTime()) / 3_600_000 : 0;
}

export function monthName(yearMonth: string): string {
  const [y, m] = yearMonth.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(new Date(y, (m || 1) - 1, 1));
}

export type Outcome = "wait" | "prog" | "ready" | "ok" | "rev" | "stop" | "set" | "res";

export const OUTCOMES: Record<Outcome, { label: string; tone: "neutral" | "pending" | "brand" | "matched" | "review" | "mismatch"; meaning: string }> = {
  // The words answer four questions: is it being compared, did it reconcile, does it need a person, is the PO
  // set to Approved in Syncore. rev, stop and set all read "Needs you"; the failed check says why.
  wait: { label: "Queued", tone: "neutral", meaning: "Not yet processed by AutoVouch." },
  prog: { label: "Comparing", tone: "pending", meaning: "Being read and compared with its PO right now." },
  ready: { label: "Ready to approve", tone: "brand", meaning: "Matched its PO. The PO is set to Approved in Syncore at the next scheduled time." },
  ok: { label: "Approved", tone: "matched", meaning: "Matched its PO. The PO is set to Approved in Syncore and the invoice attached to the job." },
  rev: { label: "Needs you", tone: "review", meaning: "Something differs from the PO. Attached to the job with a note; the PO is left for you." },
  stop: { label: "Needs you", tone: "review", meaning: "Couldn’t be reconciled against Syncore. Nothing changed in Syncore." },
  set: { label: "Needs you", tone: "review", meaning: "A Syncore setting is missing. The invoice is fine; it’s checked again once the setting is saved." },
  res: { label: "Not approved", tone: "neutral", meaning: "Rejected by a person, or replaced by a corrected invoice; the reason is kept. Nothing changed in Syncore." },
};

/** The API's status and flags as one of the outcome keys (several share a word). */
const SETUP_CODES = new Set(["MISSING_CREDENTIALS", "SYNCORE_API_401", "SYNCORE_API_403", "SYNCORE_LOGIN_FAILED"]);

export function outcomeOf(invoice: Pick<Invoice, "status" | "resolved" | "awaitingRelease" | "step"> & Partial<Pick<Invoice, "code">>): Outcome {
  if (invoice.resolved) return "res";
  switch (invoice.status as InvoiceStatus) {
    case "Vouched": return "ok";
    case "NeedsReview": return "rev";
    case "Failed": return invoice.code && SETUP_CODES.has(invoice.code) ? "set" : "stop";
    case "Processing": return "prog";
    case "Held": return invoice.awaitingRelease ? "ready" : "wait";
    default: return invoice.step ? "prog" : "wait";
  }
}

export const STEPS = ["Queued", "Reading invoice", "Finding PO", "Checking against PO", "Updating Syncore"];

/** The worker's step name in words: "Attaching invoice", "Approving PO"; an unknown step is shown as it came. */
const STEP_WORDS: Record<string, string> = { Extracting: "Reading invoice", ResolvingPo: "Finding PO", Matching: "Checking against PO", UpdatingFreight: "Updating PO freight", AddingAdjustmentLine: "Adding adjustment line", RecordingInvoiceDetails: "Recording invoice date", Attaching: "Attaching invoice", Approving: "Approving PO", Filing: "Filing" };
export function stepWords(step: string | null | undefined): string {
  if (!step) return "";
  return STEP_WORDS[step] ?? step;
}

/** The worker's step name as an index into the five progress steps. */
export function stepIndex(step: string | null | undefined): number {
  if (!step) return 0;
  const s = step.toLowerCase();
  if (s.includes("extract") || s.includes("read")) return 1;
  if (s.includes("resolv") || s.includes("find") || s.includes("po")) return 2;
  if (s.includes("match") || s.includes("check")) return 3;
  if (s.includes("attach") || s.includes("approv") || s.includes("vouch") || s.includes("syncore") || s.includes("updat") || s.includes("adjust") || s.includes("record")) return 4;
  return 1;
}

export function roleWord(role: Role, article = false): string {
  const word = role === "Staff" ? "AutoVouch staff" : role;
  if (!article) return word;
  return (role === "Admin" || role === "Approver" ? "an " : "a ") + word;
}

export function invoiceRef(invoice: Pick<Invoice, "supplier" | "invoiceNumber" | "fileName">): string {
  return [invoice.supplier, invoice.invoiceNumber].filter(Boolean).join(" – ") || invoice.fileName;
}

export function poRef(invoice: Pick<Invoice, "jobNumber" | "poNumber">): string | null {
  return invoice.jobNumber && invoice.poNumber ? `${invoice.jobNumber}-${invoice.poNumber}` : null;
}

export function tierAllowance(tier: string, allowance?: number): string {
  if (allowance) return allowance.toLocaleString("en-US");
  const m = /(\d+)/.exec(tier);
  return m ? Number(m[1]).toLocaleString("en-US") : "—";
}

export function tierName(tier: string): string {
  const m = /^Invoices(\d+)$/.exec(tier);
  return m ? Number(m[1]).toLocaleString("en-US") + " invoices" : tier;
}

export function actorName(actor: { type: string; id: string | null; name: string | null } | null | undefined): string {
  if (!actor) return "AutoVouch";
  if (actor.type === "system" || actor.type === "seed") return "AutoVouch";
  if (actor.type === "scheduler") return "AutoVouch (schedule)";
  if (actor.type === "syncore") return "Syncore";
  if (actor.type === "signup") return "sign-up";
  if (actor.type === "staff") return actor.name && !looksLikeId(actor.name) ? actor.name : "AutoVouch staff";
  const name = actor.name ?? actor.id;
  // A person the portal can't name (removed from the workspace) is still a person, never an id.
  return name && !looksLikeId(name) ? name : "a former user";
}

function looksLikeId(text: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text) || /^(user|staff):/.test(text);
}

/** Plain words for the audit event names (front/RULES.md: no codes). */
export const EVENT_WORDS: Record<string, string> = {
  InvoiceReceived: "Invoice received", Extracted: "Read", Matched: "Checked", Vouched: "PO approved", NeedsReview: "Needs review", Failed: "Can’t match",
  AttachmentUploaded: "PDF attached", AttachmentReused: "PDF already attached", AttachmentSkipped: "PDF not attached", FreightUpdated: "PO freight updated", PoApproved: "PO approved",
  AdjustmentLineAdded: "Adjustment line added", InvoiceDetailsRecorded: "Invoice date recorded", LimitWaived: "Approved over the limit",
  InvoiceReprocessed: "Check again", InvoiceDismissed: "Closed – handled outside AutoVouch", InvoiceDeleted: "Removed from the queue", InvoiceUploaded: "Uploaded", InvoiceAlreadyReceived: "Already received – not queued again", ZipUploaded: "Zip uploaded and unpacked",
  SettingsChanged: "Settings changed", WentLive: "Went live", BackToPreview: "Back to preview", SecretSet: "Secret set", SecretRemoved: "Secret removed", AddressRegenerated: "Address changed",
  AllowListChanged: "Senders changed", UserInvited: "Invited", InvitationAccepted: "Joined", UserRoleChanged: "Role changed", UserDeactivated: "Deactivated", UserDeleted: "Deleted",
  BatchReleased: "Approved now", BatchHeld: "Automatic approval paused", RetentionChanged: "Retention changed", SpendCapReached: "Spend cap reached",
  TenantOffboarded: "Offboarded", FixForMe: "Approve as invoiced", PoNumberSet: "PO number set", SupplierAccepted: "Supplier accepted", SupplierAliasForgotten: "Supplier name forgotten",
};

export function eventWords(event: string): string {
  return EVENT_WORDS[event] ?? event.replace(/([a-z])([A-Z])/g, "$1 $2");
}
